import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { RegExp } from 'src/app/core/config/regex';
import { Context } from 'src/app/shared/models/context';
import { Company, Image, Message } from 'src/app/shared/models/domain';
import { AuthResponse, AuthService } from 'src/app/shared/services/auth.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { DomainService } from 'src/app/shared/services/domain.service';
import { ProfileService } from 'src/app/shared/services/profile.service';
import { SettingsService } from 'src/app/shared/services/settings.service';

@Component({
  selector: 'app-company-profile',
  templateUrl: './company-profile.component.html',
  styleUrls: ['./company-profile.component.scss']
})
export class CompanyProfileComponent implements OnInit {
  @Input() company: Company;
  @Output() passEntry: EventEmitter<any> = new EventEmitter();
  public session: AuthResponse = {} as AuthResponse;
  public context: Context;
  public companyForm: FormGroup;
  public modified: boolean = false;
  public periodTypes: string[] = [];
  public submitted: boolean = false;
  public onLoad: boolean = false;
  public inProgress: boolean = false;
  private imageUrl: string;
  public image: string;

  constructor(
    private authService: AuthService,
    private fb: FormBuilder,
    private settingsService: SettingsService,
    private commonService: CommonService,
    private profileService: ProfileService,
    private domainService: DomainService,
    private cdr : ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.periodTypes = this.settingsService.getPeriodTypes();
    this.session = this.authService.getSession();
    this.setCompany(this.company);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['company']) {
      this.company = changes['company'].currentValue;
      this.setCompany(this.company);
    }
    this.getImage();
  }

  private setCompany(company?: Company) {
    const context: Context = this.authService.getContextData();
    if (!company) company = {
      id: null,
      name: '',
      website: '',
      contact: '',
      domain: '',
      gstIn: null,
      weblink: '',
      logo: '',
      banner: '',
      code: null,
      currencyId: context?.currencyId,
      timezoneId: context?.timezoneId,
      disabled: false,
      punchable: false,
      period: 'FISCAL_YEAR'
    } as Company;
    this.companyForm = this.fb.group({
      id: this.fb.control(company.id, [Validators.required]),
      name: this.fb.control(company.name, [Validators.required, Validators.pattern(RegExp.letters)]),
      website: this.fb.control(company.website, [Validators.required, Validators.pattern(RegExp.website)]),
      contact: this.fb.control(company.contact, [Validators.required, Validators.pattern(RegExp.text)]),
      weblink: this.fb.control(company.weblink, []),
      domain: this.fb.control(company.domain, Validators.pattern(RegExp.domain)),
      gstIn: this.fb.control(company.gstIn, [Validators.pattern(RegExp.gst)]),
      logo: this.fb.control(company.logo, []),
      banner: this.fb.control(company.banner, []),
      code: this.fb.control(company.code, [Validators.required, Validators.pattern(RegExp.code)]),
      currencyId: this.fb.control(company.currencyId, [Validators.required]),
      timezoneId: this.fb.control(company.timezoneId, [Validators.required]),
      disabled: this.fb.control(company.disabled, []),
      punchable: this.fb.control(company.punchable, []),
      period: this.fb.control(company.period, [Validators.required, Validators.pattern(RegExp.text)]),
    });
    this.companyForm.valueChanges.subscribe(value => this.modified = true);
  }

  get f() {
    return this.companyForm.controls;
  }

   public async getAndSetBase64(event: any) {
      const image: string = await this.commonService.convertImgToBase64(event);
      if (!image) return;
      this.imageUrl = image;
      this.domainService.setCompanyImage(image);
      // this.saveImage(this.company.id, 'COMPANY', 'logo', this.imageUrl);
    }
  
  
    private saveImage(sid: number, refType: string, label: string, url: string) {
      const image: Image = {
        id: null,
        sid: sid,
        type: refType,
        label: label,
        image: url,
      };
    
      this.domainService.createImage(image).subscribe({
        next: (response: string) => {
          const uploadedImageUrl = response;
          this.domainService.setCompanyImage(uploadedImageUrl);
          this.cdr.detectChanges(); 
        },
        error: (error: HttpErrorResponse) => {
        },
      });
    }
    
    private getImage() {
      this.domainService
        .getImage('COMPANY', 'COMPANY', this.company.id, 'logo')
        .subscribe(
          (image: Message) => {
            this.image=image.payload;
            this.domainService.setCompanyImage(image.payload); 
            this.setCompany(this.company);
            this.cdr.detectChanges();
          },
          (error) => {
            this.image = null; 
          }
        );
    }
    // private getImage() {
    //   this.domainService.getImage('COMPANY', 'COMPANY', this.company.id, 'logo').subscribe(image => {
    //     this.image=image.payload;
    //     this.cdr.detectChanges();
    //   });
    // }
  

  public upsertCompanyProfile() {
    this.submitted = true;
    if (this.companyForm.valid) {
      let company: Company = this.companyForm.value;
      if (!this.modified) {
        this.modified = true;
      }
      this.inProgress = true;
      if (company.id) {
        this.profileService.upsertCompanyProfile(company.id, company).subscribe({
          next: (response: Message) => {
            this.inProgress = false;
            if (!response.failed) {
              this.passEntry.emit(true);
              this.onLoad = true;
              if(this.imageUrl){
                this.saveImage(company.id, 'COMPANY', 'logo', this.imageUrl);
              } 
              this.commonService.setToastr(200, `Company ${company.name} has been updated successfully!`);

              this.modified = false;
            } else {
              this.commonService.setToastr(500, `Cannot update the record. Company '${company.name}' already exists.`);
            }
            this.submitted = false;
          },
          error: (error: HttpErrorResponse) => {
            this.inProgress = false;
            this.commonService.setToastr(500, error?.error?.name || error.error);
          },
        });
      }
    }
  }
}
